import type { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import type { Workflow, Trigger } from "@/stores/WorkflowStore";

import { logger } from "@/telemetry";

export interface ValidationResult {
  errors: string[];
  nodeErrors: Record<string, string[]>;
}

/**
 * Validates a workflow against client-side rules and the platform API.
 * Returns global errors and per-node errors.
 */
export const validateWorkflow = async (
  workflow: Workflow | null,
  platformClient: WebuildAiPlatform | null,
): Promise<ValidationResult> => {
  if (!workflow || !platformClient) {
    return { errors: [], nodeErrors: {} };
  }

  const errors: string[] = [];
  const nodeErrors: Record<string, string[]> = {};

  const addNodeError = (nodeId: string, message: string) => {
    if (!nodeErrors[nodeId]) nodeErrors[nodeId] = [];
    nodeErrors[nodeId].push(message);
    errors.push(message);
  };

  // ─── Workflow-level checks ────────────────────────────────────────────────

  if (!workflow.name?.trim()) {
    errors.push("Workflow name is required");
  }

  if (!workflow.agents || Object.keys(workflow.agents).length === 0) {
    errors.push("At least one agent is required");
  }

  // ─── Agent checks ─────────────────────────────────────────────────────────

  Object.entries(workflow.agents || {}).forEach(([agentId, agent]) => {
    if (!agent.type) {
      addNodeError(agentId, `Agent "${agentId}" is missing a type`);
    }
    if (!agent.parameters?.model) {
      addNodeError(agentId, `Agent "${agentId}" is missing a model`);
    }
  });

  // ─── Team checks ──────────────────────────────────────────────────────────

  if (workflow.teams) {
    Object.entries(workflow.teams).forEach(([teamId, team]) => {
      if (!team.members || team.members.length === 0) {
        addNodeError(teamId, `Team "${teamId}" has no members`);
      }
      if (!team.goal?.trim()) {
        addNodeError(teamId, `Team "${teamId}" is missing a goal`);
      }

      // Check members reference existing agents or teams
      team.members.forEach((memberId) => {
        const memberExists = workflow.agents?.[memberId] || workflow.teams?.[memberId];

        if (!memberExists) {
          addNodeError(teamId, `Team "${teamId}" references unknown member "${memberId}"`);
        }
      });
    });
  }

  // ─── Trigger checks ───────────────────────────────────────────────────────

  // Check trigger sources exist
  const allNodeIds = new Set([
    "input",
    ...Object.keys(workflow.agents || {}),
    ...Object.keys(workflow.teams || {}),
  ]);

  Object.entries(workflow.agents || {}).forEach(([agentId, agent]) => {
    (agent.triggers || []).forEach((trigger: Trigger) => {
      if (!allNodeIds.has(trigger.source)) {
        addNodeError(
          agentId,
          `Agent "${agentId}" has a trigger from unknown source "${trigger.source}"`,
        );
      }
    });
  });

  if (workflow.teams) {
    Object.entries(workflow.teams).forEach(([teamId, team]) => {
      (team.triggers || []).forEach((trigger: Trigger) => {
        if (!allNodeIds.has(trigger.source)) {
          addNodeError(
            teamId,
            `Team "${teamId}" has a trigger from unknown source "${trigger.source}"`,
          );
        }
      });
    });
  }

  // ─── Circular dependency detection ────────────────────────────────────────

  const circularErrors = detectCircularTriggers(workflow);

  circularErrors.forEach((err) => errors.push(err));

  // ─── Orphan node detection ────────────────────────────────────────────────

  // Nodes with no incoming triggers and not "input"
  const nodesWithIncoming = new Set<string>();

  Object.values(workflow.agents || {}).forEach((agent) => {
    (agent.triggers || []).forEach((trigger) => nodesWithIncoming.add(trigger.source));
  });
  if (workflow.teams) {
    Object.values(workflow.teams).forEach((team) => {
      (team.triggers || []).forEach((trigger) => nodesWithIncoming.add(trigger.source));
      // Team members are implicitly connected
      team.members.forEach((m) => nodesWithIncoming.add(m));
    });
  }

  // Collect team member IDs
  const teamMemberIds = new Set<string>();

  if (workflow.teams) {
    const teamIds = new Set(Object.keys(workflow.teams));

    Object.values(workflow.teams).forEach((team) => {
      team.members.forEach((m) => {
        if (!teamIds.has(m)) teamMemberIds.add(m);
      });
    });
  }

  // Check non-team-member agents and teams have either incoming triggers or outgoing triggers
  const hasAnyTrigger = (nodeId: string): boolean => {
    // Has incoming trigger from somewhere
    const hasIncoming = Object.values(workflow.agents || {}).some((agent) =>
      agent.triggers?.some((t: Trigger) => t.source === nodeId),
    );

    if (hasIncoming) return true;

    if (workflow.teams) {
      const hasTeamIncoming = Object.values(workflow.teams).some((team) =>
        team.triggers?.some((t: Trigger) => t.source === nodeId),
      );

      if (hasTeamIncoming) return true;
    }

    // Has outgoing triggers
    const agent = workflow.agents?.[nodeId];

    if (agent?.triggers && agent.triggers.length > 0) return true;

    const team = workflow.teams?.[nodeId];

    if (team?.triggers && team.triggers.length > 0) return true;

    return false;
  };

  // Non-team-member agents that are top-level
  const topLevelAgentIds = Object.keys(workflow.agents || {}).filter(
    (id) => !teamMemberIds.has(id),
  );
  const topLevelTeamIds = Object.keys(workflow.teams || {});
  const totalTopLevelNodes = topLevelAgentIds.length + topLevelTeamIds.length;

  topLevelAgentIds.forEach((agentId) => {
    // A single top-level node without explicit triggers is valid — the
    // runtime uses a linear fallback (input → agent → output).
    if (totalTopLevelNodes === 1) return;
    if (!hasAnyTrigger(agentId)) {
      addNodeError(agentId, `Agent "${agentId}" is disconnected (no triggers)`);
    }
  });

  // ─── Skip server validation if client checks found errors ─────────────────

  if (errors.length > 0) {
    return { errors, nodeErrors };
  }

  // ─── Server-side validation ───────────────────────────────────────────────

  try {
    await platformClient.workflows.validate({
      workflow,
    });
  } catch (error: any) {
    logger.error("Workflow validation failed", error);
    const message = error?.body?.detail || error?.message || "Workflow validation failed";

    errors.push(message);
  }

  return { errors, nodeErrors };
};

/**
 * Detects circular trigger dependencies in a workflow.
 * Uses DFS to find cycles in the trigger graph.
 */
function detectCircularTriggers(workflow: Workflow): string[] {
  const errors: string[] = [];

  // Build adjacency list: source → targets
  const graph = new Map<string, string[]>();

  Object.entries(workflow.agents || {}).forEach(([agentId, agent]) => {
    (agent.triggers || []).forEach((trigger: Trigger) => {
      const targets = graph.get(trigger.source) || [];

      targets.push(agentId);
      graph.set(trigger.source, targets);
    });
  });

  if (workflow.teams) {
    Object.entries(workflow.teams).forEach(([teamId, team]) => {
      (team.triggers || []).forEach((trigger: Trigger) => {
        const targets = graph.get(trigger.source) || [];

        targets.push(teamId);
        graph.set(trigger.source, targets);
      });
    });
  }

  // DFS cycle detection
  const visited = new Set<string>();
  const inStack = new Set<string>();

  const dfs = (node: string, path: string[]): boolean => {
    if (inStack.has(node)) {
      const cycleStart = path.indexOf(node);
      const cycle = path.slice(cycleStart).concat(node);

      errors.push(`Circular dependency detected: ${cycle.join(" → ")}`);

      return true;
    }

    if (visited.has(node)) return false;

    visited.add(node);
    inStack.add(node);

    const neighbours = graph.get(node) || [];

    for (const neighbour of neighbours) {
      if (dfs(neighbour, [...path, node])) return true;
    }

    inStack.delete(node);

    return false;
  };

  for (const node of graph.keys()) {
    if (!visited.has(node)) {
      dfs(node, []);
    }
  }

  return errors;
}
